import React, { Suspense, lazy } from "react";
import { BrowserRouter, Navigate, Outlet, Route, Routes, useLocation } from "react-router-dom";
import { Toaster } from "./components/uikit";
import { Header } from "./components/Header";
import { SplashScreen } from "./components/SplashScreen";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { ScanProvider } from "./context/ScanContext";
import { ThemeProvider } from "./context/ThemeContext";
import { ErrorBoundary } from "./components/ErrorBoundary";
import "./App.css";

// Every page used to be a static import, so a first-time visitor's initial
// bundle included AdminPage, PolicyPage and every other route most sessions
// never touch. React.lazy + the Suspense boundary below splits each page
// into its own chunk, fetched only when its route is actually visited.
const LoginPage = lazy(() => import("./pages/LoginPage"));
const ScanPage = lazy(() => import("./pages/ScanPage"));
const DashboardPage = lazy(() => import("./pages/DashboardPage"));
const PolicyPage = lazy(() => import("./pages/PolicyPage"));
const PricingPage = lazy(() => import("./pages/PricingPage"));
const TrackRecordPage = lazy(() => import("./pages/TrackRecordPage"));
const AccountPage = lazy(() => import("./pages/AccountPage"));
const AdminPage = lazy(() => import("./pages/AdminPage"));
const PaymentStatusPage = lazy(() => import("./pages/PaymentStatusPage"));
const SupportPage = lazy(() => import("./pages/SupportPage"));
const HowItWorksPage = lazy(() => import("./pages/HowItWorksPage"));

// Dev-only design review harness (see pages/DesignPreviewPage.tsx).
//
// Deliberately a guarded `require` rather than a `lazy(() => import(...))`:
// webpack registers a dynamic import even when the branch that renders it is
// statically dead, so the lazy form still emitted a production chunk carrying
// the harness and its fixture - unreachable, but shipped. A require inside an
// `if` that DefinePlugin resolves to false is skipped by webpack's parser
// entirely, so neither the page nor the fixture reaches a production build.
const IS_DEV = process.env.NODE_ENV !== "production";
let DesignPreviewPage: React.ComponentType | null = null;
if (IS_DEV) {
  DesignPreviewPage = require("./pages/DesignPreviewPage").default;
}

// React Router doesn't reset scroll position on navigation the way a normal
// full-page load does - a full-page browser navigation always starts at the
// top, but client-side route changes just swap the page content in place,
// leaving the window wherever it was scrolled on the previous page. Land on
// Login scrolled to the bottom of a long page, then navigate to Scan or How
// It Works, and you'd land mid-page instead of at the top. This resets
// scroll on every route change; disabling the browser's own scroll
// restoration keeps it from fighting this on back/forward navigation too.
const ScrollToTop: React.FC = () => {
  const { pathname } = useLocation();
  React.useEffect(() => {
    if ("scrollRestoration" in window.history) {
      window.history.scrollRestoration = "manual";
    }
  }, []);
  React.useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [pathname]);
  return null;
};

const RouteLoadingFallback: React.FC = () => (
  <div className="flex min-h-screen items-center justify-center">
    <div className="text-center">
      <span className="font-wordmark text-3xl text-[hsl(var(--ink))]">Financify</span>
      <p className="mt-2 animate-pulse text-xs font-bold uppercase tracking-[0.2em] text-[hsl(var(--muted-ink))]">Loading Tenet...</p>
    </div>
  </div>
);

const ProtectedLayout: React.FC = () => {
  const { session, initialising } = useAuth();
  const location = useLocation();
  if (initialising) return <RouteLoadingFallback />;
  if (!session) return <Navigate to="/login" replace state={{ from: location }} />;
  return (
    <>
      <Header />
      <main>
        <Outlet />
      </main>
    </>
  );
};

// Pricing, Support and Track Record are public (reachable while signed out),
// but also routinely reached by a signed-in user mid-session via a footer
// link. Unlike ProtectedLayout, this never redirects - Header itself already
// renders correctly for a null session (just the logo, no account menu) - so
// a signed-out visitor sees the same minimal chrome as before, while a
// signed-in visitor keeps their way back to the dashboard instead of hitting
// a page with no nav at all.
const PublicHeaderLayout: React.FC = () => (
  <>
    <Header />
    <main>
      <Outlet />
    </main>
  </>
);

// The full brand splash (quote rotation) is a deliberate first-impression
// moment - but it played identically on every page load, not just the first
// one in a browser session. A returning user hitting refresh to check one
// number paid the same toll as a brand-new visitor. sessionStorage (not
// localStorage) marks "already seen this splash in this browser session" -
// a fresh tab/window, or the same tab after being closed and reopened,
// still gets the full moment.
//
// The first-visit delay itself was also cut from ~5.6s to ~2.4s: a scan's
// core action (type a ticker, click Analyse) is meant to be self-evident,
// and a moving quote a user can't interact with or skip was pure timer
// friction stacked in front of it, on top of the onboarding tour and "New
// here?" banner that already explain the product right after.
const SPLASH_SEEN_KEY = "honey_splash_seen_session_v1";

function hasSeenSplashThisSession(): boolean {
  try {
    return window.sessionStorage.getItem(SPLASH_SEEN_KEY) === "1";
  } catch {
    return false; // Fail open - worst case, show the full splash again.
  }
}

function markSplashSeenThisSession() {
  try {
    window.sessionStorage.setItem(SPLASH_SEEN_KEY, "1");
  } catch {
    // Best-effort only - a private/incognito tab losing this just means
    // the full splash plays again next reload, not a broken app.
  }
}

export default function AppRoot() {
  const isRepeatVisit = React.useMemo(hasSeenSplashThisSession, []);
  const [showSplash, setShowSplash] = React.useState(true);
  const [splashLeaving, setSplashLeaving] = React.useState(false);

  React.useEffect(() => {
    // removeDelay - leaveDelay must stay >= the 600ms opacity transition on
    // .splash-screen (App.css) so showSplash doesn't unmount the element
    // mid-fade.
    const leaveDelay = isRepeatVisit ? 250 : 2000;
    const removeDelay = isRepeatVisit ? 550 : 2600;
    const leave = window.setTimeout(() => setSplashLeaving(true), leaveDelay);
    const remove = window.setTimeout(() => setShowSplash(false), removeDelay);
    markSplashSeenThisSession();
    return () => {
      window.clearTimeout(leave);
      window.clearTimeout(remove);
    };
  }, [isRepeatVisit]);

  return (
    <ErrorBoundary>
      <ThemeProvider>
        <AuthProvider>
          <ScanProvider>
            <BrowserRouter>
              <ScrollToTop />
              <Suspense fallback={<RouteLoadingFallback />}>
                <Routes>
                  {DesignPreviewPage ? <Route path="/design-preview" element={<DesignPreviewPage />} /> : null}
                  <Route path="/login" element={<LoginPage />} />
                  <Route path="/terms" element={<PolicyPage />} />
                  <Route path="/privacy" element={<PolicyPage />} />
                  <Route path="/pricing-credits-refunds" element={<PolicyPage />} />
                  <Route path="/investment-disclaimer" element={<PolicyPage />} />
                  <Route path="/contact-grievance" element={<PolicyPage />} />
                  <Route element={<PublicHeaderLayout />}>
                    <Route path="/pricing" element={<PricingPage />} />
                    <Route path="/track-record" element={<TrackRecordPage />} />
                    <Route path="/support" element={<SupportPage />} />
                  </Route>
                  <Route element={<ProtectedLayout />}>
                    <Route path="/scan" element={<ScanPage />} />
                    <Route path="/how-it-works" element={<HowItWorksPage />} />
                    <Route path="/dashboard" element={<DashboardPage />} />
                    <Route path="/account" element={<AccountPage />} />
                    <Route path="/payment-status" element={<PaymentStatusPage />} />
                    <Route path="/admin" element={<AdminPage />} />
                  </Route>
                  <Route path="*" element={<Navigate to="/scan" replace />} />
                </Routes>
              </Suspense>
            </BrowserRouter>
            <Toaster position="top-right" richColors closeButton />
            {showSplash ? <SplashScreen leaving={splashLeaving} /> : null}
          </ScanProvider>
        </AuthProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}
